import HttpService from 'src/services/HttpService';

export type PlaylistType = 'custom' | 'watch_later';

export type PlaylistContentType = 'session' | 'challenge' | 'meditation';

export type PlaylistSummary = {
  id: string;
  name: string;
  description: string | null;
  playlistType: PlaylistType;
  isPrivate: boolean;
  itemCount: number;
  updatedDate: string;
};

export type PlaylistItemSource = 'session' | 'challenge' | 'challenge_session';

export type PlaylistItemResolved = {
  id: string;
  contentId: string;
  contentType: PlaylistContentType;
  position: number;
  title: string;
  thumbnailUrl: string | null;
  source: PlaylistItemSource;
};

export type PlaylistDetail = {
  id: string;
  name: string;
  description: string | null;
  playlistType: PlaylistType;
  isPrivate: boolean;
  items: PlaylistItemResolved[];
  progress: {
    lastPlayedItemId: string | null;
    completedItemIds: string[];
    updatedDate: string;
  };
};

export type PlaylistStatus = {
  inWatchLater: boolean;
  watchLaterItemId: string | null;
  playlistIds: string[];
};

const base = '/playlists';

const PlaylistsApi = {
  list: (): Promise<PlaylistSummary[]> => HttpService.get(base),

  getOne: (id: string): Promise<PlaylistDetail> =>
    HttpService.get(`${base}/${id}`),

  create: (body: {
    name: string;
    description?: string;
    isPrivate?: boolean;
  }): Promise<PlaylistSummary> => HttpService.post(base, body),

  update: (
    id: string,
    body: { name?: string; description?: string; isPrivate?: boolean },
  ): Promise<PlaylistSummary> => HttpService.patch(`${base}/${id}`, body),

  remove: (id: string): Promise<void> =>
    HttpService.delete(`${base}/${id}`),

  addItem: (
    playlistId: string,
    body: { contentId: string; contentType: PlaylistContentType },
  ): Promise<{ id: string }> =>
    HttpService.post(`${base}/${playlistId}/items`, body),

  removeItem: (playlistId: string, itemId: string): Promise<void> =>
    HttpService.delete(`${base}/${playlistId}/items/${itemId}`),

  reorderItems: (
    playlistId: string,
    body: { itemIds: string[] },
  ): Promise<void> =>
    HttpService.patch(`${base}/${playlistId}/items/reorder`, body),

  getStatus: (
    contentId: string,
    contentType: PlaylistContentType,
  ): Promise<PlaylistStatus> =>
    HttpService.get(
      `${base}/status?contentId=${encodeURIComponent(
        contentId,
      )}&contentType=${encodeURIComponent(contentType)}`,
    ),

  updateProgress: (
    playlistId: string,
    body: {
      lastPlayedItemId?: string | null;
      completedItemIds?: string[];
    },
  ): Promise<{ lastPlayedItemId: string | null; completedItemIds: string[]; updatedDate: string }> =>
    HttpService.patch(`${base}/${playlistId}/progress`, body),
};

export default PlaylistsApi;
